import { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { initializeFomoCommunities } from '@/utils/initializeCommunities';

/**
 * Hook that automatically initializes FOMO communities when the app starts
 * This ensures communities exist for all users without manual initialization
 */
export function useAutoCommunityInitialization() {
  const queryClient = useQueryClient();

  // Check if communities exist
  const { data: existingCommunities } = useQuery({
    queryKey: ['check-fomo-communities-exist'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('communities')
        .select('count')
        .eq('is_fomo_official', true)
        .single();

      if (error) {
        // If table doesn't exist yet or other error, return 0
        console.error('Error checking for communities:', error);
        return { count: 0 };
      }
      
      return data;
    }
  });

  // Auto-initialize communities if none exist
  useEffect(() => {
    const autoInitialize = async () => {
      const count = existingCommunities?.count || 0;
      
      if (count === 0) {
        console.log('No FOMO communities found. Auto-initializing...');
        
        try {
          await initializeFomoCommunities();
          // Invalidate any queries that depend on communities
          queryClient.invalidateQueries({ queryKey: ['all-communities'] });
          queryClient.invalidateQueries({ queryKey: ['sidebar-communities'] });
        } catch (error) {
          console.error('Failed to auto-initialize communities:', error);
        }
      }
    };

    if (existingCommunities !== undefined) {
      autoInitialize();
    }
  }, [existingCommunities, queryClient]);
}
